/**
 * Gift Reveal Modal - Elegant Animation
 * ======================================
 * Premium animation for receiving gifts with luxury styling.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, User, Gift } from 'lucide-react';

const GiftRevealModal = ({ 
  isOpen, 
  onClose, 
  gift,
  senderName,
  senderPhoto,
  recipientHasCard = false
}) => {
  const navigate = useNavigate();
  const [animationPhase, setAnimationPhase] = useState(0);
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setAnimationPhase(0);
      // Phase 1: Glow appears
      setTimeout(() => setAnimationPhase(1), 100);
      // Phase 2: Card reveals
      setTimeout(() => setAnimationPhase(2), 600);
      // Phase 3: Content fades in
      setTimeout(() => setAnimationPhase(3), 1200);
    }
  }, [isOpen]);

  if (!isOpen || !gift) return null;

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const getCategoryEmoji = (category) => {
    if (!category) return '🎁';
    if (category.includes('dinner')) return '🍽';
    if (category.includes('drink')) return '🍸';
    if (category.includes('spa')) return '💆';
    if (category.includes('bottle')) return '🍾';
    if (category.includes('shop')) return '🛍';
    if (category.includes('travel')) return '✈️';
    if (category.includes('entertainment')) return '🎟';
    if (category.includes('celebration')) return '🎉';
    return '✨';
  };

  const getCategoryName = (category) => {
    if (!category) return 'Gift';
    return category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleActivateCard = () => {
    onClose();
    navigate('/gifts', { state: { openCardActivation: true } });
  };

  const handleViewGift = () => {
    onClose();
    navigate('/gift-activity');
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      {/* Animated Background with Gold Glow */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: animationPhase >= 1 
            ? 'radial-gradient(circle at center, rgba(215, 181, 109, 0.15) 0%, rgba(0, 0, 0, 0.95) 70%)'
            : 'rgba(0, 0, 0, 0.95)',
          transition: 'background 0.8s ease-out',
          backdropFilter: 'blur(12px)'
        }}
        onClick={onClose}
      />

      {/* Gold Particle Effect */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: animationPhase >= 1 
          ? 'radial-gradient(circle, rgba(215, 181, 109, 0.3) 0%, transparent 60%)'
          : 'transparent',
        opacity: animationPhase >= 1 ? 1 : 0,
        transition: 'opacity 0.8s ease-out',
        pointerEvents: 'none',
        animation: animationPhase >= 2 ? 'pulse 3s ease-in-out infinite' : 'none'
      }} />

      {/* Gift Card */}
      <div 
        data-testid="gift-reveal-card"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '380px',
          background: isDark 
            ? 'linear-gradient(145deg, #1A0F0A 0%, #2C1B13 50%, #1A0F0A 100%)'
            : 'linear-gradient(145deg, #FFFFFF 0%, #F5F0E8 50%, #FFFFFF 100%)',
          borderRadius: '24px',
          overflow: 'hidden',
          transform: animationPhase >= 2 ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(30px)',
          opacity: animationPhase >= 2 ? 1 : 0,
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          boxShadow: `
            0 0 60px rgba(215, 181, 109, 0.3),
            0 25px 50px rgba(0, 0, 0, 0.5),
            inset 0 1px 1px rgba(255, 255, 255, 0.1)
          `,
          border: '1px solid rgba(215, 181, 109, 0.3)'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.4)',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.7)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          <X size={20} />
        </button>

        {/* Gold Border Accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, transparent, #D7B56D, #A57C33, #D7B56D, transparent)'
        }} />

        {/* Content */}
        <div style={{
          padding: '40px 28px',
          textAlign: 'center',
          opacity: animationPhase >= 3 ? 1 : 0,
          transform: animationPhase >= 3 ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.5s ease-out 0.2s'
        }}>
          {/* Gift Icon with Glow */}
          <div style={{
            width: '90px',
            height: '90px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(215, 181, 109, 0.05) 100%)',
            border: '2px solid rgba(215, 181, 109, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 0 40px rgba(215, 181, 109, 0.3)',
            fontSize: '44px'
          }}>
            {getCategoryEmoji(gift.category)}
          </div>

          {/* Title */}
          <h2 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '24px',
            color: isDark ? '#E6D7C3' : '#1A1A1A',
            marginBottom: '8px',
            fontWeight: '600'
          }}>
            You received a gift
          </h2>

          {/* Amount */}
          <div style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '42px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 50%, #D7B56D 100%)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
            animation: 'shimmer 3s ease-in-out infinite'
          }}>
            {formatCurrency(gift.gross_gift_amount_cents || gift.amount_cents)}
          </div>

          {/* Category */}
          <div style={{
            fontSize: '16px',
            color: isDark ? '#A57C33' : '#8B6914',
            marginBottom: '24px',
            fontWeight: '500'
          }}>
            {getCategoryName(gift.category)} Gift
          </div>

          {/* Sender Info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '16px',
            background: isDark 
              ? 'rgba(215, 181, 109, 0.08)'
              : 'rgba(215, 181, 109, 0.12)',
            borderRadius: '14px',
            marginBottom: '16px'
          }}>
            {senderPhoto ? (
              <img 
                src={senderPhoto} 
                alt={senderName}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(215, 181, 109, 0.4)'
                }}
              />
            ) : (
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'rgba(215, 181, 109, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(215, 181, 109, 0.4)'
              }}>
                <User size={24} color="#D7B56D" />
              </div>
            )}
            <div style={{ textAlign: 'left' }}>
              <div style={{ 
                color: isDark ? '#8A7355' : '#666666', 
                fontSize: '12px',
                marginBottom: '2px'
              }}>
                From
              </div>
              <div style={{ 
                color: isDark ? '#E6D7C3' : '#1A1A1A', 
                fontSize: '16px',
                fontWeight: '600'
              }}>
                {senderName || 'Someone special'}
              </div>
            </div>
          </div>

          {/* Message */}
          {gift.message && (
            <div style={{
              padding: '16px',
              background: isDark 
                ? 'rgba(215, 181, 109, 0.05)'
                : 'rgba(215, 181, 109, 0.08)',
              borderRadius: '12px',
              borderLeft: '3px solid #D7B56D',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <p style={{
                color: isDark ? '#E6D7C3' : '#1A1A1A',
                fontSize: '15px',
                fontStyle: 'italic',
                margin: 0,
                lineHeight: '1.5'
              }}>
                "{gift.message}"
              </p>
            </div>
          )}

          {/* Action based on card status */}
          {recipientHasCard ? (
            <button
              onClick={handleViewGift}
              data-testid="view-gift-btn"
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                border: 'none',
                borderRadius: '14px',
                color: '#2C1B13',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Gift size={20} />
              View Your Gift
            </button>
          ) : (
            <>
              {/* Card Activation Required */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px',
                background: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '10px',
                marginBottom: '16px'
              }}>
                <Lock size={16} color="#FFC107" />
                <span style={{ 
                  color: '#FFC107', 
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  Activate your NearU Card to access gifts
                </span>
              </div>

              <button
                onClick={handleActivateCard}
                data-testid="activate-card-btn"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  color: '#2C1B13',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)'
                }}
              >
                GET YOUR NEARU CARD
              </button>
            </>
          )}
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default GiftRevealModal;
